import { useSelector } from 'react-redux';
import { RootState } from 'Store/index';
import { Addon, Feature, isAvailable, isAvailableAddon } from 'App/services/FeatureHelperStandalone';

type ContactScraperRoleConfig = {
	filterEnabled: boolean;
	allowedRoleIds: number[];
};

export const parseContactScraperRoleFilter = (configString: string | null | undefined): ContactScraperRoleConfig => {
	const defaultConfig: ContactScraperRoleConfig = {
		filterEnabled: false,
		allowedRoleIds: []
	};

	if (!configString || configString === '{}') {
		return defaultConfig;
	}

	try {
		const parsed = JSON.parse(configString);

		if (typeof parsed !== 'object' || parsed === null) {
			return defaultConfig;
		}

		const filterEnabled = typeof parsed.filterEnabled === 'boolean' ? parsed.filterEnabled : false;

		let allowedRoleIds: number[] = [];
		if (Array.isArray(parsed.allowedRoleIds)) {
			allowedRoleIds = parsed.allowedRoleIds.filter((id: unknown) => typeof id === 'number' && id > 0);
		}

		return {
			filterEnabled,
			allowedRoleIds
		};
	} catch {
		return defaultConfig;
	}
};

/**
 * Pure function to check contact scraper access based on role filtering.
 * Assumes addon check is already done.
 */
export const checkContactScraperRoleAccess = (opts: {
	configString: string | null | undefined;
	isAdmin: boolean;
	userRoleId: number | null | undefined;
}): boolean => {
	const config = parseContactScraperRoleFilter(opts.configString);

	if (!config.filterEnabled) {
		return true;
	}

	if (opts.isAdmin) {
		return true;
	}

	if (config.allowedRoleIds.length === 0) {
		return false;
	}

	if (!opts.userRoleId) {
		return false;
	}

	return config.allowedRoleIds.includes(opts.userRoleId);
};

/**
 * Whether the ACCOUNT holds the contact scraper at all: through the AI_CONTACT_SCRAPER addon,
 * or through the Upsales AI tier, which grants it as part of the base and is no longer sold the
 * addon. Mirrors `canUseContactScraper` in base/src/core/services/contactScraper.ts — the gate
 * the API applies before it accepts a run — so every surface that offers the scraper asks this
 * one function rather than restating the rule. `checkContactScraperRoleAccess` layers on top and
 * decides which users inside such an account may run it.
 *
 * Synchronous rather than a hook so the Angular company card and the company-list multi-action
 * can share it; flags are loaded once at boot and constant for the session.
 */
export const hasContactScraperAccountAccess = (): boolean =>
	isAvailableAddon(Addon.AI_CONTACT_SCRAPER) || isAvailable(Feature.AI_ASSISTANT);

/**
 * Hook to check if the current user has access to the Contact Scraper feature.
 * Checks both account-level access and role-based restrictions.
 */
export const useContactScraperAccess = (): { hasAccess: boolean } => {
	const self = useSelector((state: RootState) => state.App.self);
	const params = useSelector((state: RootState) => state.App.metadata?.params);

	if (!hasContactScraperAccountAccess()) {
		return { hasAccess: false };
	}

	const hasAccess = checkContactScraperRoleAccess({
		configString: params?.ContactScraperRoleFilter as string | undefined,
		isAdmin: !!self?.administrator,
		userRoleId: self?.role?.id
	});

	return { hasAccess };
};

export default useContactScraperAccess;
